import React from 'react';
import { List, TextField, ToggleSwitch } from '../../../../components/fields';
import Heading from '../../../../_global/components/heading/Heading';
import './_styles.scss';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider
} from '@mui/material';
import { Bank, CardsItemProps, Network, SpendingCategory } from '..';
import AwardsRow from '../../../../_global/components/awards-row/AwardsRow';
import { parseFloatWithDecimal } from '../../../../utilities/stringUtils';
import { awardsDataService } from '../../../awards/services';
import { MainPrograms } from '../../../awards/components';
import { useStateReducer } from '../../../../hooks';
import { UserPermissions } from '../../../../../src/definitions';
import {  securityService } from '../../../../../src/_global/services';

export function groupBy(xs: any[], key: string) {
  return xs.reduce(function (rv: any, x: any) {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
}

export default function ViewItem({
  data: {
    _id,
    name,
    signupBonus,
    cashBonus,
    minimumSpend,
    daysToSpend,
    annual_fee,
    cashPerks,
    cardFamily,
    rewards_currency,
    bank,
    network,
    websiteUrl,
    referralCardLink,
    spendingCategories,
    mxGuid,
    annualFeeWaived,
    bisCard,
    chargeCard,
    colour
  }
}: CardsItemProps) {
  const [state, setState] = useStateReducer({
    rewards_currency
  });

  spendingCategories &&
    spendingCategories.forEach((element: SpendingCategory) => {
      element.multiplier = parseFloatWithDecimal(
        element.multiplier.toString(),
        2
      );
    });

  let groupedBy: SpendingCategory[] = groupBy(
    spendingCategories!,
    'multiplier'
  );
  const spendingCategoriesKeys = Object.keys(groupedBy) as Array<
    keyof SpendingCategory
  >;

  spendingCategoriesKeys.sort(
    (a: string, b: string) => parseFloat(b) - parseFloat(a)
  );

  React.useEffect(() => {
    (async function fetchSpendCategories() {
      let p: MainPrograms[] = await awardsDataService.getMainPrograms();

      let rewardDisplayName = '';
      p.forEach((element: MainPrograms) => {
        if (element.main_program === rewards_currency)
          rewardDisplayName = element.display_program ?? '';
      });

      setState({
        rewardDisplayName: rewardDisplayName
      });
    })();
  }, [rewards_currency, setState]);

  let mainp: MainPrograms = {
    main_program: rewards_currency ?? '',
    display_program: state.rewardDisplayName ?? ''
  };
  return (
    <div id='container-item' className='read-only'>
      <Heading name={name} style={{ margin: ' 0px' }} />
      <div className='grid'>
        <TextField label='Identifier' value={_id} readOnly />
        <div></div>
      </div>
      <div className='grid'>
        <TextField label='Card Name' value={name} readOnly />
        { securityService.checkRoleAccess(UserPermissions.viewDefaultCardLink) ?       
          ( <TextField label='Default Card Link' value={websiteUrl} readOnly />): null
        }       
      </div>

      { securityService.checkRoleAccess(UserPermissions.viewReferralCardLink) ?
        (
          <div className='grid'>
            <p></p>               
            <TextField label='Referral Card Link' value={referralCardLink} readOnly />
          </div>
        ):null
      }       
     
      <div className='grid'>
        <TextField label='Signup Bonus' value={signupBonus} readOnly />
        <TextField label='cashBonus' value={cashBonus} readOnly />
      </div>

      <div className='grid'>
        <TextField label='minimumSpend' value={minimumSpend} readOnly />
        <TextField label='daysToSpend' value={daysToSpend} readOnly />
      </div>

      <div className='grid'>
        <TextField label='annual_fee' value={annual_fee} readOnly />
        <TextField label='cashPerks' value={cashPerks} readOnly />
      </div>
      <div className='grid'>
        <TextField label='cardFamily' value={cardFamily} readOnly />
        <TextField label='Card Color' value={colour} type={'color'} readOnly />
      </div>
      <div className='grid'>
        <ToggleSwitch
          value={annualFeeWaived}
          readOnly
          label={'Annual Fee Waived'}
          name={'annualFeeWaived'}
        ></ToggleSwitch>
        <ToggleSwitch
          value={bisCard}
          readOnly
          label={'Business Card'}
          name={'bisCard'}
        ></ToggleSwitch>
      </div>
      <div className='grid'>
        <ToggleSwitch
          value={chargeCard}
          readOnly
          label={'Charge Card'}
          name={'chargeCard'}
        ></ToggleSwitch>
        <div></div>
      </div>

      <Divider />
      <div className='grid'>
        <List
          readOnly
          className='top-margin'
          label={`Network`}
          value={network}
          options={[network]}
          getOptionLabel={(o: Network) => {
            return `${o.name}`;
          }}
          getOptionValue={(o: Network) => {
            return `${o.name}`;
          }}
        />
        <List
          readOnly
          className='top-margin'
          label={`Bank`}
          value={bank}
          options={[bank]}
          getOptionLabel={(o: Bank) => {
            return `${o.name}`;
          }}
          getOptionValue={(o) => {
            return `${o}`;
          }}
        />
      </div>
      <List
        readOnly
        className='top-margin'
        label={`Reward`}
        value={mainp}
        options={[mainp]}
        getOptionLabel={(o: MainPrograms) => {
          return o.display_program ?? '';
        }}
        getOptionValue={(o: MainPrograms) => {
          return o.display_program ?? '';
        }}
      />

      <Divider />
      <br />

      <Heading name='MX Guid' />
      <div className='grid'>
        <TextField
          readOnly
          label={'MX Guid'}
          value={mxGuid?.mx_cca_guid ?? ''}
        ></TextField>
      </div>
      <Heading name='Spending Categories' />

      {spendingCategoriesKeys.map(
        (groupedMultiplier: keyof SpendingCategory, index: number) => {
          return (
            <Accordion expanded={true}>
              <AccordionSummary aria-controls='panel1d-content'>
                <h2
                  style={{
                    color: 'white',
                    opacity: '0.8',
                    fontSize: '16px'
                  }}
                >
                  {groupedMultiplier} x Multiplier
                </h2>
              </AccordionSummary>
              {spendingCategories && (
                <AccordionDetails>
                  {spendingCategories
                    .filter((element: SpendingCategory) => {
                      return (
                        parseFloat(groupedMultiplier) === element.multiplier
                      );
                    })
                    .map((val: SpendingCategory) => {
                      return (
                        <AwardsRow
                          readOnly={true}
                          key={val.spend_category}
                          name={val.spend_category}
                          value={val.multiplier.toString()}
                          options={[val.spend_category]}
                        />
                      );
                    })}
                </AccordionDetails>
              )}
            </Accordion>
          );
        }
      )}
    </div>
  );
}
